import {
  type SortDirection,
  compareNullsLast,
  sortFactor,
} from "~/pages/manifold-nextgen/components/evals/TableSort";
import {
  SCORE_BAND_ORDER,
  ScoreBand,
} from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import { RunSortKey } from "~/pages/manifold-nextgen/components/evals/constants/sorting";
import type { PartialEvalCaseResult, PartialEvalRun } from "~/services/evals";
import {
  type ResultsByRun,
  caseMeanScores,
  parseApiTimestamp,
  runDurationSeconds,
  scoreBandFor,
} from "./core";
import { mean } from "./math";

/** A run as the runs tab reads it: one row, one trend point, one distribution. */
export interface RunSummary {
  /** Result rows, i.e. cases x iterations — not distinct cases. */
  resultCount: number;
  distinctCaseCount: number;
  passed: number;
  failed: number;
  errored: number;
  /** Judged results only: a `passed: null` result had nothing to judge. */
  judged: number;
  /** `passed / judged`, or null when the run judged nothing. */
  passRate: number | null;
  /**
   * Mean of the run's *per-case* scores, collapsed to the case before averaging so
   * a change in iteration count cannot move the figure on its own.
   */
  meanScore: number | null;
}
export function summarizeRun(results: PartialEvalCaseResult[]): RunSummary {
  const judged = results.filter((result) => result.passed != null);
  const passed = judged.filter((result) => result.passed).length;
  const errored = results.filter((result) => result.error != null).length;

  return {
    resultCount: results.length,
    distinctCaseCount: new Set(results.map((result) => result.case_id)).size,
    passed,
    failed: judged.length - passed,
    errored,
    judged: judged.length,
    passRate: judged.length > 0 ? passed / judged.length : null,
    meanScore: mean(caseMeanScores(results)),
  };
}
export interface TrendPoint {
  runId: string;
  startedAt: string;
  /** Percent, 0-100. Runs that judged nothing are excluded, never plotted as 0. */
  passRatePercent: number;
  /** Mean of the run's case scores on the 1-4 rubric. */
  meanScore: number | null;
  /**
   * Lowest and highest *per-case* mean score in the run, drawn as the spread band.
   * Per case, so one unlucky iteration cannot stretch it to the floor.
   */
  minCaseScore: number | null;
  maxCaseScore: number | null;
  judged: number;
}
/**
 * The score series, oldest first, with the per-run spread of case scores. A run
 * that judged nothing is left out rather than plotted as zero, which would invent
 * a regression. The spread is what separates a run where every case scored 3.2
 * from one where half scored 4 and half 2.4.
 */
export function buildTrend(runs: PartialEvalRun[], resultsByRun: ResultsByRun): TrendPoint[] {
  return runs.flatMap((run) => {
    const results = resultsByRun[run.id] ?? [];
    const summary = summarizeRun(results);
    if (summary.passRate == null) {
      return [];
    }
    const caseScores = caseMeanScores(results);
    return [
      {
        runId: run.id,
        startedAt: run.started_at,
        passRatePercent: summary.passRate * 100,
        meanScore: summary.meanScore,
        minCaseScore: caseScores.length > 0 ? Math.min(...caseScores) : null,
        maxCaseScore: caseScores.length > 0 ? Math.max(...caseScores) : null,
        judged: summary.judged,
      },
    ];
  });
}
export interface ScoreBucket {
  band: ScoreBand;
  caseCount: number;
}
/**
 * The run's cases counted into the four rubric bands, best first. Every band is
 * present even at zero, so the histogram keeps one shape run to run — and the
 * empty bands are the finding when a suite is healthy.
 */
export function buildScoreDistribution(results: PartialEvalCaseResult[]): ScoreBucket[] {
  const scores = caseMeanScores(results);
  return SCORE_BAND_ORDER.map((band) => ({
    band,
    caseCount: scores.filter((score) => scoreBandFor(score) === band).length,
  }));
}
export interface RunTableRow {
  run: PartialEvalRun;
  /** Null for a run outside the measured window, which judged nothing is not. */
  summary: RunSummary | null;
  durationSeconds: number | null;
}
/**
 * Joins each run with its summary so sorting and rendering read one shape. A run
 * `resultsByRun` has no entry for was never measured; an empty entry was.
 */
export function buildRunRows(runs: PartialEvalRun[], resultsByRun: ResultsByRun): RunTableRow[] {
  return runs.map((run) => {
    const results = resultsByRun[run.id];
    return {
      run,
      summary: results ? summarizeRun(results) : null,
      durationSeconds: runDurationSeconds(run),
    };
  });
}
/** Row order for the runs table. Nulls last, for the reason `sortCaseRows` gives. */
export function sortRunRows(
  rows: RunTableRow[],
  key: RunSortKey,
  direction: SortDirection
): RunTableRow[] {
  const factor = sortFactor(direction);
  return [...rows].sort((a, b) => {
    switch (key) {
      case RunSortKey.STARTED:
        return factor * (parseApiTimestamp(a.run.started_at) - parseApiTimestamp(b.run.started_at));
      case RunSortKey.PASS_RATE:
        return compareNullsLast(a.summary?.passRate ?? null, b.summary?.passRate ?? null, factor);
      case RunSortKey.MEAN_SCORE:
        return compareNullsLast(a.summary?.meanScore ?? null, b.summary?.meanScore ?? null, factor);
      case RunSortKey.CASES:
        return compareNullsLast(
          a.summary?.distinctCaseCount ?? null,
          b.summary?.distinctCaseCount ?? null,
          factor
        );
      case RunSortKey.DURATION:
        return compareNullsLast(a.durationSeconds, b.durationSeconds, factor);
      case RunSortKey.STATUS:
      default:
        return factor * a.run.status.localeCompare(b.run.status);
    }
  });
}
